<template>
  <div
    :class="{'missing-translation': hadMissingTranslation, 'mb-8': depth === 0}"
    class="document-node">
    <template v-if="depth === 0">
      <div class="flex mr-8">
        <div class="flex-1"/>
        <div
          v-for="lang in langs"
          :key="lang"
          class="flex-1 pl-2">
          <div
            :class="colors"
            class="p-2 text-center rounded-t-sm">
            {{ lang }}
          </div>
        </div>
      </div>
    </template>
    <div class="flex pb-2">
      <div class="flex-1">
        <div
          :style="{'margin-left': Math.max(depth - 1, 0) * 2 + 'rem'}"
          :class="[{'cursor-pointer': node.childNodes.length > 0 }, colors]"
          class="p-2 pl-4 rounded-sm"
          @click="show = !show">
          {{ node.title }}
        </div>
      </div>
      <template v-if="node.childNodes.length === 0">
        <div
          v-for="lang in langs"
          :key="lang"
          class="flex-1 pl-2">
          <textarea
            ref="textarea"
            v-model="cachedEntry[lang]"
            :class="{ 'border-red-500': !cachedEntry[lang] || cachedEntry[lang].includes('***') }"
            rows="1"
            class="border rounded w-full p-2 block"
            @blur="textareaBlur"
            @focus="textareaResize"
            @input="textareaResize"
            @keydown.enter.prevent="$event.target.blur()"/>
        </div>
      </template>
      <div class="pl-2 text-2xl self-end w-8">
        <button
          v-if="pictureURL"
          class="w-full"
          style="outline:none"
          @click="showPicture = true">
          🌇
        </button>
        <button
          v-else
          class="w-full text-gray-300 hover:text-gray-900"
          style="outline:none"
          @click="() => $refs.fileInput.click()">
          +
        </button>
      </div>
    </div>
    <template v-if="show">
      <div>
        <document-node
          v-for="n in node.childNodes"
          :key="n.title"
          :node="n"
          :path="path.concat(node.title)"
          :langs="langs"
          :pictures="pictures"/>
      </div>
    </template>
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      @input="ev => { uploadFile(ev.target.files[0]); ev.target.value = '' }">
    <modal
      v-if="showPicture"
      padding="0"
      @close="showPicture = false">
      <img
        :src="pictureURL"
        class="m-auto">
      <button
        class="w-full rounded-b bg-gray-600 hover:bg-red-600 text-white p-2"
        @click="removeFile">
        delete
      </button>
    </modal>
  </div>
</template>

<script>
  import Modal from '@/components/Modal'
  import firebase from 'firebase'
  import { pick } from 'lodash'

  export default {
    name: 'DocumentNode',

    props: {
      node: {
        type: Object,
        required: true
      },
      langs: {
        type: Array,
        required: true
      },
      pictures: {
        type: Object,
        required: true
      },
      path: {
        type: Array,
        default: () => []
      }
    },

    data () {
      return {
        cachedEntry: {},
        uploadedPictureURL: null,
        showPicture: false,
        show: false
      }
    },

    computed: {
      depth () {
        return this.path.length
      },
      pictureName () {
        return encodeURIComponent(this.path.concat(this.node.title).join('!'))
      },
      pictureURL () {
        if (this.uploadedPictureURL === '') return null
        return this.uploadedPictureURL || (this.pictures[this.pictureName] || [{}])[0].downloadURL
      },
      pictureRef () {
        return firebase.app().storage().ref().child(`projects/${this.$route.params.id}/pictures`).child(this.pictureName)
      },
      colors () {
        const n = this.depth === 0 ? 300 : 0
        return [{ 'text-white': this.hadMissingTranslation || this.depth === 0 }, this.hasMissingTranslation ? `bg-red-${500 + n}` : this.hadMissingTranslation ? `bg-green-${500 + n}` : `bg-gray-${300 + n}`]
      },
      hasMissingTranslation () {
        return this.node.entries.some(entry => this.langs.some(lang => !entry[lang] || entry[lang].includes('***')))
      },
      hadMissingTranslation () {
        return this.node.entries.some(entry => entry._initially_missing) || this.hasMissingTranslation
      }
    },

    watch: {
      'node.entries': {
        handler (value) {
          this.cachedEntry = { ...this.node.entries[0] }
          if (this.hadMissingTranslation) this.show = true
        },
        immediate: true
      }
    },

    methods: {
      async uploadFile (file) {
        this.uploadedPictureURL = await this.pictureRef.put(file)
          .then(file => file.ref.getDownloadURL())
      },
      async removeFile () {
        await this.pictureRef.delete()
        this.showPicture = false
        this.uploadedPictureURL = ''
      },
      textareaResize () {
        const minHeight = Math.max(...this.$refs.textarea.map(textarea => textarea.scrollHeight)) + 'px'
        this.$refs.textarea.forEach(textarea => { textarea.style.minHeight = minHeight })
      },
      textareaBlur () {
        if (this.langs.every(lang => this.cachedEntry[lang] === this.node.entries[0][lang])) return
        this.cachedEntry._ref.update(pick(this.cachedEntry, this.langs))
      }
    },

    components: {
      Modal
    }
  }
</script>
